import { useEffect, useState } from "react";
import { toast } from "sonner";
import { changeCurrentUserPassword } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { clearAuthSession } from "@/lib/api/token-store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const MIN_PASSWORD_CHARACTERS = 8;

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorText, setErrorText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrorText("");
    setIsSubmitting(false);
  }, [open]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorText("Complete all password fields.");
      return;
    }
    if (Array.from(newPassword).length < MIN_PASSWORD_CHARACTERS) {
      setErrorText("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorText("New passwords don't match.");
      return;
    }

    setIsSubmitting(true);
    setErrorText("");
    try {
      await changeCurrentUserPassword({ currentPassword, newPassword });
      toast.success("Password changed.");
      onOpenChange?.(false);
    } catch (error) {
      console.error("[Auth] password change failed:", error);
      if (error instanceof ApiError && error.status === 401) {
        if (String(error.payload?.error || "") === "unauthorized") {
          onOpenChange?.(false);
          clearAuthSession();
          toast.error("Your session has expired. Please sign in again.");
          return;
        }
        setErrorText("Current password is incorrect.");
      } else if (error instanceof ApiError && error.status === 400) {
        setErrorText(String(error.payload?.error || "The new password is not valid."));
      } else if (error instanceof ApiError) {
        setErrorText("Password change is unavailable right now.");
      } else {
        setErrorText("Could not connect to the server. Try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearError = () => {
    if (errorText) setErrorText("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>
            Enter your current password, then choose a new one with at least 8 characters.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            type="password"
            value={currentPassword}
            onChange={(event) => {
              setCurrentPassword(event.target.value);
              clearError();
            }}
            placeholder="Current password"
            autoComplete="current-password"
            autoFocus
            disabled={isSubmitting}
          />
          <Input
            type="password"
            value={newPassword}
            onChange={(event) => {
              setNewPassword(event.target.value);
              clearError();
            }}
            placeholder="New password"
            autoComplete="new-password"
            disabled={isSubmitting}
          />
          <Input
            type="password"
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              clearError();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void handleSubmit();
              }
            }}
            placeholder="Confirm new password"
            autoComplete="new-password"
            disabled={isSubmitting}
          />
          {errorText ? <p className="text-sm text-destructive">{errorText}</p> : null}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={isSubmitting}>
            {isSubmitting ? "Changing…" : "Change password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
